/**
 * « CE DOCUMENT M'ATTEND-IL POUR SIGNER ? » — module PUR (aucun import d'exécution).
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Le tableau de bord listait sous « À vérifier » tout document en revue dès que
 * la personne portait le RÔLE `verificateur` — même quand un autre vérificateur
 * était DÉSIGNÉ, et même quand elle l'avait rédigé (03/10/2026 : Asma Ezzi voyait
 * IT-PRE-003, qu'elle a rédigé et dont Inès est la vérificatrice désignée). Le
 * serveur refuse pourtant ce geste (séparation des tâches, ISO 15189 §8.3) :
 * l'écran proposait ce que le serveur interdit.
 *
 * Règle, la MÊME que celle du serveur et du centre de tâches :
 *  · jamais pour qui a RÉDIGÉ le document (contributeurs, rédacteur de la version,
 *    rédacteurs affectés) ;
 *  · si un signataire est DÉSIGNÉ, c'est lui seul ;
 *  · sinon, quiconque porte le rôle — c'est le seul cas où le rôle suffit ;
 *  · à l'approbation, le vérificateur ne peut pas approuver ce qu'il a vérifié.
 */
import type { GedDocument } from './types';

type Doc = Pick<GedDocument, 'statut' | 'redacteur' | 'redacteurs' | 'verificateur' | 'approbateur' | 'versions'>;
type Personne = { key: string; roles: string[] };

/** Qui a rédigé : MÊME base que `redacteursDe` du serveur et que DocDetail. */
export function aRedige(doc: Doc, cle: string): boolean {
  const cand = (doc.versions || []).find((v) => !v.obsolete && !v.dateVigueur);
  const l = [...(cand?.contributeurs || []), cand?.redacteur, doc.redacteur, ...(doc.redacteurs || [])];
  return l.filter(Boolean).includes(cle);
}

/**
 * DÉROGATION DU RQ (03/10/2026) : approuver un document qu'on a rédigé. Elle exige
 * le rôle `rq`, une vérification faite par UNE AUTRE personne (qui n'a pas rédigé),
 * et un motif écrit au moment de signer. Même règle que le moteur (`approve`).
 */
export function peutApprouverSonTexte(doc: Doc, moi: Personne): boolean {
  if (!moi.key || doc.statut !== 'aapprouver' || !moi.roles.includes('rq')) return false;
  if (!aRedige(doc, moi.key)) return false;
  return !!doc.verificateur && doc.verificateur !== moi.key && !aRedige(doc, doc.verificateur);
}

export function attendDeMoi(doc: Doc, moi: Personne): 'verification' | 'approbation' | null {
  if (!moi.key) return null;
  if (aRedige(doc, moi.key)) {
    // Seule voie : la dérogation du RQ à l'approbation, quand l'approbateur c'est lui (ou personne).
    return peutApprouverSonTexte(doc, moi) && (!doc.approbateur || doc.approbateur === moi.key) ? 'approbation' : null;
  }
  if (doc.statut === 'averifier') {
    if (doc.verificateur) return doc.verificateur === moi.key ? 'verification' : null;
    return moi.roles.includes('verificateur') ? 'verification' : null;
  }
  if (doc.statut === 'aapprouver') {
    if (doc.verificateur === moi.key) return null;
    if (doc.approbateur) return doc.approbateur === moi.key ? 'approbation' : null;
    return moi.roles.includes('approbateur') ? 'approbation' : null;
  }
  return null;
}

/**
 * Le document m'est DÉSIGNÉ pour signer, mais la séparation des tâches me l'interdit
 * (j'ai contribué au contenu, ou — à l'approbation — je l'ai vérifié). Le cacher
 * serait pire que le montrer : il resterait en revue sans que personne ne sache
 * qu'il est bloqué (03/10/2026 : la RQ, vérificatrice désignée d'IT-PRE-003, avait
 * importé et enregistré le fichier — elle ne pouvait donc plus le vérifier, et il
 * avait disparu de sa liste). On l'affiche, marqué, avec la sortie : désigner
 * quelqu'un d'autre.
 */
export function signatureBloquee(doc: Doc, moi: Personne): 'verification' | 'approbation' | null {
  if (!moi.key) return null;
  if (doc.statut === 'averifier' && doc.verificateur === moi.key && aRedige(doc, moi.key)) return 'verification';
  if (doc.statut === 'aapprouver' && doc.approbateur === moi.key && !peutApprouverSonTexte(doc, moi) && (aRedige(doc, moi.key) || doc.verificateur === moi.key)) return 'approbation';
  return null;
}

